import { getUserPreferences, updateUserPreferences } from "../../api";
import React, { useRef, useState, useEffect } from "react";
import {
  User,
  Briefcase,
  Building2,
  Mail,
  Phone,
  MapPin,
  Globe,
  Twitter,
  Facebook,
  Linkedin,
  Instagram,
  Pencil,
  X,
  BriefcaseBusiness,
  Landmark,
} from "lucide-react";
import RoleDropdown from "../RoleDropdown";
import OfficeDropdown from "../OfficeDropdown";
import Cookies from "js-cookie";
import axios from "axios";

const formatCategory = (category) => {
  if (!category) return "—";

  return category
    .replace(/_/g, " ")
    .split(" ")
    .map((word) =>
      word.toLowerCase() === "edi"
        ? "EDI"
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .join(" ");
};

const ViewUserProfile = ({ userData: initialUserData }) => {
  const [userData, setUserData] = useState(initialUserData);
  const [formData, setFormData] = useState(initialUserData || {});
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [preferences, setPreferences] = useState(null);
  const [preferencesLoading, setPreferencesLoading] = useState(false);
  const [updatingPreferences, setUpdatingPreferences] = useState(false);
  const scrollContainerRef = useRef(null);

  const token = Cookies.get("authToken");
  const officeCode = Cookies.get("officeCode");

  // 🔁 Fetch latest profile
  const refreshUserProfile = async () => {
    if (!initialUserData?.email) return;
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/auth/user/viewprofile`,
        {
          params: { userEmail: initialUserData.email },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setUserData(res.data);
      setFormData(res.data);
      console.log("✅ Refreshed user profile:", res.data);
    } catch (err) {
      console.error("❌ Failed to refresh user profile:", err);
    } finally {
      setLoading(false);
    }
  };

  //Update user profile
  const handleSaveProfile = async () => {
    try {
      setLoading(true);
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/user/updateprofile`,
        formData,
        {
          params: { userEmail: userData.email },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      console.log("✅ Profile updated successfully");
      await refreshUserProfile();
      setIsEditing(false);
    } catch (err) {
      console.error("❌ Failed to update profile:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadUserPreferences = async () => {
    if (!userData?.email) return;
    try {
      setPreferencesLoading(true);
      const res = await getUserPreferences();
      setPreferences(res.data);
    } catch (err) {
      console.error("Failed to fetch preferences:", err);
    } finally {
      setPreferencesLoading(false);
    }
  };

  // 🔧 Update preference immediately
  const handleTogglePreference = async (pref) => {
    try {
      setUpdatingPreferences(true);
      await updateUserPreferences(pref.category, !pref.isEnabled);
      // Update local preferences state
      setPreferences((prevPreferences) =>
        prevPreferences.map((p) =>
          p.id === pref.id ? { ...p, isEnabled: !p.isEnabled } : p
        )
      );
      console.log("✅ Preference updated successfully");
    } catch (err) {
      console.error("❌ Failed to update preference:", err);
    } finally {
      setUpdatingPreferences(false);
    }
  };

  useEffect(() => {
    refreshUserProfile();
  }, [initialUserData?.email]);

  useEffect(() => {
    if (userData?.email) {
      loadUserPreferences();
    }
  }, [userData?.email]);

  if (loading)
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-acs-dark-subtext">
        Loading profile...
      </div>
    );

  if (!userData)
    return (
      <div className="p-6 text-gray-500 dark:text-acs-dark-subtext">
        No profile data available.
      </div>
    );

  const fullName = `${userData.firstName || ""} ${userData.lastName || ""}`.trim();

  const renderAvatar = () => {
    if (userData.profilePicture)
      return (
        <img
          src={userData.profilePicture}
          alt="Profile"
          className="w-24 h-24 rounded-full object-cover shadow-sm"
        />
      );
    const initials = (userData.firstName?.[0] || "U") + (userData.lastName?.[0] || "");
    return (
      <div className="w-24 h-24 rounded-full bg-acs-accent/20 dark:bg-acs-accent/30 flex items-center justify-center text-2xl font-semibold text-acs-accent shadow-inner">
        {initials}
      </div>
    );
  };

  const infoIcons = {
    firstName: <User className="w-4 h-4 text-gray-400 dark:text-gray-500" />,
    lastName: <User className="w-4 h-4 text-gray-400 dark:text-gray-500" />,
    email: <Mail className="w-4 h-4 text-gray-400 dark:text-gray-500" />,
    job: <BriefcaseBusiness className="w-4 h-4 text-gray-400 dark:text-gray-500" />,
    industry: <Landmark className="w-4 h-4 text-gray-400 dark:text-gray-500" />,
    phoneNumber: <Phone className="w-4 h-4 text-gray-400 dark:text-gray-500" />,
    address: <MapPin className="w-4 h-4 text-gray-400 dark:text-gray-500" />,
    country: <Globe className="w-4 h-4 text-gray-400 dark:text-gray-500" />,
    twitter: <Twitter className="w-4 h-4 text-gray-400 dark:text-gray-500" />,
    facebook: <Facebook className="w-4 h-4 text-gray-400 dark:text-gray-500" />,
    linkedin: <Linkedin className="w-4 h-4 text-gray-400 dark:text-gray-500" />,
    instagram: <Instagram className="w-4 h-4 text-gray-400 dark:text-gray-500" />,
  };

  return (
    <div
      ref={scrollContainerRef}
      className="w-full h-full min-h-0 flex flex-col transition-opacity duration-500 bg-gray-50 dark:bg-acs-dark-bg"
    >
      {/* === Top Header === */}
      <div className="sticky top-0 z-10 bg-white dark:bg-acs-dark-surface shadow-md p-fluid-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-fluid-3">
        <div className="flex items-center gap-fluid-3">
          {renderAvatar()}
          <div>
            <h2 className="text-fluid-xl font-semibold text-gray-900 dark:text-acs-dark-text">
              {fullName}
            </h2>
            <p className="text-sm text-gray-500 dark:text-acs-dark-subtext">
              {userData.companyName || "No company info"}
            </p>
            <div className="mt-fluid-1 flex items-center gap-2">
              <span className="bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-2 py-[2px] text-xs rounded-md font-medium">
                Active
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* === Two Column Layout === */}
      <div className="flex-1 overflow-y-auto bg-white dark:bg-acs-dark-bg w-full p-fluid-3 grid grid-cols-1 md:grid-cols-2 gap-fluid-3">
        {/* LEFT COLUMN */}
        <div className="space-y-fluid-3">
          {/* === Contact Information === */}
          <div
            className={`bg-gray-50 dark:bg-acs-dark-surface border border-gray-100 dark:border-acs-dark-border rounded-xl shadow-sm p-fluid-3 ${
              isEditing ? "ring-2 ring-indigo-300 dark:ring-acs-accent" : ""
            }`}
          >
            <div className="flex items-center justify-between mb-fluid-2">
              <h3 className="text-fluid-lg font-semibold text-gray-900 dark:text-acs-dark-text">
                Contact Information
              </h3>
              <button
                onClick={() => setIsEditing((prev) => !prev)}
                className="text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-acs-accent transition"
              >
                {isEditing ? <X className="w-5 h-5" /> : <Pencil className="w-5 h-5" />}
              </button>
            </div>

            {isEditing ? (
              <div className="space-y-3 text-fluid-base text-gray-700 dark:text-acs-dark-text">
                {[
                  "firstName",
                  "lastName",
                  "job",
                  "industry",
                  "phoneNumber",
                  "address",
                  "country",
                  "twitter",
                  "facebook",
                  "linkedin",
                  "instagram",
                ].map((field) => (
                  <div key={field}>
                    <label className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-acs-dark-subtext mb-1 capitalize">
                      {infoIcons[field]}
                      {field}
                    </label>
                    <input
                      type="text"
                      value={formData[field] || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, [field]: e.target.value }))
                      }
                      placeholder={`Enter ${field}`}
                      className="w-full border border-gray-300 dark:border-acs-dark-border rounded-md p-2 text-sm bg-white dark:bg-[#2A2D35] text-gray-800 dark:text-acs-dark-text focus:ring-2 focus:ring-indigo-400 dark:focus:ring-acs-accent focus:outline-none"
                    />
                  </div>
                ))}

                {/* Non-editable email */}
                <div>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-acs-dark-subtext mb-1">
                    {infoIcons.email}
                    Email
                  </label>
                  <input
                    type="text"
                    value={userData.email}
                    disabled
                    className="w-full border border-gray-200 dark:border-acs-dark-border rounded-md p-2 text-sm bg-gray-100 dark:bg-[#1F2128] text-gray-500 dark:text-acs-dark-subtext"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    onClick={() => setIsEditing(false)}
                    className="px-4 py-2 text-sm rounded-md border border-gray-300 dark:border-acs-dark-border hover:bg-gray-100 dark:hover:bg-acs-dark-hover"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveProfile}
                    className="px-4 py-2 text-sm rounded-md bg-indigo-600 dark:bg-acs-accent text-white hover:bg-indigo-700 dark:hover:bg-acs-accent/80"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3 text-fluid-base text-gray-700 dark:text-acs-dark-text">
                {[
                  ["firstName", userData.firstName],
                  ["lastName", userData.lastName],
                  ["email", userData.email],
                  ["job", userData.job],
                  ["industry", userData.industry],
                  ["phoneNumber", userData.phoneNumber],
                  ["address", userData.address],
                  ["country", userData.country],
                  ["twitter", userData.twitter],
                  ["facebook", userData.facebook],
                  ["linkedin", userData.linkedin],
                  ["instagram", userData.instagram],
                ].map(([field, value]) => (
                  <div key={field} className="flex items-center gap-2">
                    {infoIcons[field]}
                    <p>
                      <span className="font-medium text-gray-600 dark:text-acs-dark-subtext capitalize">
                        {field}:
                      </span>{" "}
                      {value || "—"}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* === Roles === */}
          <div className="bg-gray-50 dark:bg-acs-dark-surface border border-gray-100 dark:border-acs-dark-border rounded-xl shadow-sm p-fluid-3">
            <div className="flex items-center justify-between mb-fluid-2">
              <div className="flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-indigo-600 dark:text-acs-accent" />
                <h3 className="text-fluid-lg font-semibold text-gray-900 dark:text-acs-dark-text">
                  Roles
                </h3>
              </div>
              <RoleDropdown
                userEmail={userData.email}
                userRoles={userData.roles}
                onRoleChange={refreshUserProfile}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {userData.roles?.length ? (
                userData.roles.map((role) => (
                  <span
                    key={role.id}
                    className="bg-indigo-50 dark:bg-[#22368833] border border-indigo-200 dark:border-acs-dark-border text-indigo-700 dark:text-acs-accent px-3 py-1 rounded-md text-xs font-medium"
                  >
                    {role.name}
                  </span>
                ))
              ) : (
                <p className="text-sm text-gray-500 dark:text-acs-dark-subtext">
                  No roles assigned
                </p>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="space-y-fluid-3">
          {/* === Organizations === */}
          <div className="bg-gray-50 dark:bg-acs-dark-surface border border-gray-100 dark:border-acs-dark-border rounded-xl shadow-sm p-fluid-3">
            <div className="flex items-center justify-between mb-fluid-2">
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600 dark:text-acs-accent" />
                <h3 className="text-fluid-lg font-semibold text-gray-900 dark:text-acs-dark-text">
                  Organizations
                </h3>
              </div>
              <OfficeDropdown
                userEmail={userData.email}
                userOrganizations={userData.organizations}
                officeCode={officeCode}
                onOfficeChange={refreshUserProfile}
              />
            </div>
            <div className="space-y-2 text-sm">
              {userData.organizations?.length ? (
                userData.organizations.map((org) => (
                  <div
                    key={org.officeCode}
                    className="border border-gray-100 dark:border-acs-dark-border rounded-md p-2 bg-white dark:bg-[#22242B]"
                  >
                    <p className="font-medium text-gray-800 dark:text-acs-dark-text">
                      {org.organizationName}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-acs-dark-subtext">
                      {org.officeCode}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 dark:text-acs-dark-subtext">
                  No organizations assigned
                </p>
              )}
            </div>
          </div>

          {/* === Notification Preferences === */}
          <div
            className="bg-gray-50 dark:bg-acs-dark-surface border border-gray-100 dark:border-acs-dark-border rounded-xl shadow-sm p-fluid-3"
          >
            <div className="flex items-center justify-between mb-fluid-2">
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-600 dark:text-acs-accent" />
                <h3 className="text-fluid-lg font-semibold text-gray-900 dark:text-acs-dark-text">
                  Email Notification Preferences
                </h3>
              </div>
            </div>
            {preferencesLoading ? (
              <p className="text-sm text-gray-500 dark:text-acs-dark-subtext">
                Loading preferences...
              </p>
            ) : (
              <div className="space-y-2 text-sm">
                {preferences.map((pref, idx) => (
                  <div
                    key={pref.id || idx}
                    className="flex items-center justify-between border border-gray-200 dark:border-acs-dark-border rounded-md p-3 bg-white dark:bg-[#22242B]"
                  >
                    <p className="font-medium text-gray-900 dark:text-acs-dark-text">
                      {formatCategory(pref.category)}
                    </p>
                    <button
                      onClick={() => handleTogglePreference(pref)}
                      disabled={updatingPreferences}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50 ${
                        pref.isEnabled
                          ? "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/60"
                          : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
                      }`}
                    >
                      {pref.isEnabled ? "Enabled" : "Disabled"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewUserProfile;
